'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import {
  ACCESS_RESTRICTED_MESSAGE,
  INVITE_EMAIL_NOT_ALLOWED_CODE,
  REGISTRATION_DISABLED_MESSAGE,
} from '@/lib/registrationConfig';
import { getEmailConfirmationRedirectUrl } from '@/lib/siteUrl';

const AuthContext = createContext<any>({});

export type SignUpResult =
  | { status: 'authenticated'; data: unknown }
  | { status: 'confirmation_required' }
  | { status: 'already_exists' };

export const ACCOUNT_ALREADY_EXISTS_CODE = 'ACCOUNT_ALREADY_EXISTS';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const router = useRouter();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    metadata: any = {}
  ): Promise<SignUpResult> => {
    const normalizedEmail = email.trim().toLowerCase();

    // Private beta UX precheck (authoritative gate remains the Auth Hook)
    try {
      const checkRes = await fetch('/api/auth/check-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const checkPayload = await checkRes.json().catch(() => ({}));
      if (!checkRes.ok || checkPayload?.allowed !== true) {
        throw new Error(
          typeof checkPayload?.code === 'string' && checkPayload.code
            ? checkPayload.code
            : INVITE_EMAIL_NOT_ALLOWED_CODE
        );
      }
    } catch (err) {
      if (err instanceof Error) throw err;
      throw new Error(REGISTRATION_DISABLED_MESSAGE);
    }

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || metadata?.full_name || '',
          avatar_url: metadata?.avatarUrl || metadata?.avatar_url || '',
        },
        emailRedirectTo: getEmailConfirmationRedirectUrl(),
      },
    });

    if (error) {
      throw error;
    }

    // Supabase returns no error but empty identities when the email already exists
    // (happens when "Confirm email" is enabled and the address is already registered)
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      return { status: 'already_exists' };
    }

    // Email confirmation required ONLY when there is NO session returned.
    // Return a success result — do not throw (throws were mis-handled as failures).
    if (data?.user && !data.session) {
      return { status: 'confirmation_required' };
    }

    if (!data?.user) {
      throw new Error(REGISTRATION_DISABLED_MESSAGE);
    }

    return { status: 'authenticated', data };
  };

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    if (error) throw error;

    // Stay-signed-in gate only. Workspace access is enforced by middleware/API —
    // unpaid public accounts must remain signed in so they can complete checkout.
    try {
      const accessRes = await fetch('/api/auth/verify-access', { credentials: 'include' });
      if (!accessRes.ok) {
        await supabase.auth.signOut();
        throw new Error(ACCESS_RESTRICTED_MESSAGE);
      }
      const payload = await accessRes.json();
      if (payload?.allowed !== true) {
        await supabase.auth.signOut();
        throw new Error(ACCESS_RESTRICTED_MESSAGE);
      }
    } catch (err) {
      if (err instanceof Error && err.message === ACCESS_RESTRICTED_MESSAGE) {
        throw err;
      }
      await supabase.auth.signOut();
      throw new Error(ACCESS_RESTRICTED_MESSAGE);
    }

    router.refresh();
    return data;
  };

  const signOut = async () => {
    // Clear identity immediately so chrome never falls back to demo/stale names mid-logout.
    setUser(null);
    setSession(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } finally {
      setLoading(false);
    }
    router.refresh();
    router.push('/sign-up-login-screen');
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, full_name, avatar_url, created_at, updated_at')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
